// components/profile/ProfileView.jsx
import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, Building, Check, AlertCircle, Shield, 
  Download, LogOut, UserX, Send 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = '/api';

// Email Verification Banner Component
const EmailVerificationBanner = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  const resendVerification = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setMessage(data.message);
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      setMessage('Errore nell\'invio dell\'email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-yellow-600 mr-3" />
          <div>
            <p className="text-yellow-800 font-medium">Email non verificata</p>
            <p className="text-yellow-700 text-sm">
              Controlla la tua email e clicca sul link di verifica per completare la registrazione.
            </p>
          </div>
        </div>
        <button
          onClick={resendVerification}
          disabled={loading}
          className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
        >
          {loading ? (
            <div className="animate-spin rounded-full border-2 border-white border-t-transparent w-4 h-4" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {loading ? 'Invio...' : 'Reinvia'}
        </button>
      </div>
      {message && (
        <p className={`mt-2 text-sm ${message.includes('Errore') ? 'text-red-600' : 'text-green-600'}`}>
          {message}
        </p>
      )}
    </div>
  );
};

// Delete Account Modal Component
const DeleteAccountModal = ({ onClose, token, onDeleted }) => {
  const [form, setForm] = useState({ confirmPassword: '', confirmDeletion: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async (e) => {
    e.preventDefault();
    if (form.confirmDeletion !== 'DELETE') {
      setError('Scrivi "DELETE" per confermare');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/users/me`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      const data = await response.json();

      if (data.success) {
        onDeleted();
        onClose();
      } else {
        setError(data.message || 'Errore eliminazione account');
      }
    } catch {
      setError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-6 rounded-2xl max-w-md w-full">
        <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center gap-2">
          <UserX className="w-6 h-6" />
          Elimina Account
        </h3>
        
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
          <p className="text-red-800 text-sm">
            <strong>Attenzione:</strong> Questa azione è irreversibile. 
            Tutti i tuoi dati, prenotazioni e impostazioni verranno eliminati permanentemente.
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleDelete} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conferma la tua password
            </label>
            <input
              type="password"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500"
              value={form.confirmPassword}
              onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scrivi "DELETE" per confermare
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500"
              placeholder="DELETE"
              value={form.confirmDeletion}
              onChange={e => setForm({ ...form, confirmDeletion: e.target.value })}
            />
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-xl font-semibold"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full border-2 border-white border-t-transparent w-5 h-5" />
              ) : (
                <UserX className="w-5 h-5" />
              )}
              {loading ? 'Eliminazione...' : 'Elimina'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main ProfileView Component
const ProfileView = () => {
  const { user, token, logout } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    currentPassword: '',
    newPassword: ''
  });
  const [gdprConsents, setGdprConsents] = useState({
    marketingConsent: false,
    profilingConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const showMsg = (m) => { setMsg(m); setTimeout(() => setMsg(null), 4000); };
  const showErr = (m) => { setErr(m); setTimeout(() => setErr(null), 5000); };

  // Carica dati profilo
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          const userData = data.data;
          setForm(f => ({
            ...f,
            name: userData.name,
            email: userData.email,
            phone: userData.phone || '',
            company: userData.company || ''
          }));
          setGdprConsents({
            marketingConsent: userData.gdprConsents?.marketingConsent?.accepted || false,
            profilingConsent: userData.gdprConsents?.profilingConsent?.accepted || false
          });
        } else {
          showErr(data.message || 'Errore nel caricamento profilo');
        }
      } catch {
        showErr('Errore di connessione');
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [token]);

  // Salva profilo
  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        company: form.company
      };
      if (form.newPassword) {
        payload.currentPassword = form.currentPassword;
        payload.newPassword = form.newPassword;
      }

      const response = await fetch(`${API_BASE}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        showMsg('Profilo aggiornato con successo');
        setForm(f => ({ ...f, currentPassword: '', newPassword: '' }));
      } else {
        showErr(data.message || 'Aggiornamento fallito');
      }
    } catch {
      showErr('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  // Aggiorna consensi GDPR
  const updateGdprConsents = async (consentType, value) => {
    try {
      const response = await fetch(`${API_BASE}/users/gdpr-consents`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ [consentType]: value })
      });
      const data = await response.json();

      if (data.success) {
        setGdprConsents(prev => ({ ...prev, [consentType]: value }));
        showMsg('Consenso aggiornato');
      } else {
        showErr(data.message || 'Errore aggiornamento consenso');
      }
    } catch {
      showErr('Errore di connessione');
    }
  };

  // Esporta dati (GDPR)
  const exportData = async () => {
    try {
      const response = await fetch(`${API_BASE}/users/data-export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        const blob = new Blob([JSON.stringify(data.data, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dati-personali-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showMsg('Dati esportati con successo');
      } else {
        showErr(data.message || 'Errore esportazione dati');
      }
    } catch {
      showErr('Errore di connessione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full border-4 border-blue-600 border-t-transparent w-8 h-8" />
        <span className="ml-4 text-gray-600">Caricamento profilo...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {msg && (
        <div className="p-4 rounded-xl bg-green-100 text-green-800 border border-green-200 flex items-center gap-2">
          <Check className="w-5 h-5" />
          <span>{msg}</span>
        </div>
      )}
      {err && (
        <div className="p-4 rounded-xl bg-red-100 text-red-800 border border-red-200 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{err}</span>
        </div>
      )}

      {/* Banner verifica email */}
      {user && !user.emailVerified && <EmailVerificationBanner />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Informazioni Profilo */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white p-6 rounded-2xl shadow-xl border">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Informazioni Personali</h2>
            
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <div className="relative">
                    <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      disabled
                      className="w-full pl-10 pr-4 py-3 border bg-gray-50 text-gray-500 border-gray-300 rounded-xl"
                      value={form.email}
                      readOnly
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">L'email non è modificabile</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <div className="relative">
                    <Phone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="tel"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      placeholder="+39 XXX XXXXXXX"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Azienda</label>
                  <div className="relative">
                    <Building className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      value={form.company}
                      onChange={e => setForm({ ...form, company: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Cambio Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password attuale</label>
                  <input
                    type="password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    placeholder="Solo per cambio password"
                    value={form.currentPassword}
                    onChange={e => setForm({ ...form, currentPassword: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nuova password</label>
                  <input
                    type="password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    placeholder="Lascia vuoto se non vuoi cambiarla"
                    value={form.newPassword}
                    onChange={e => setForm({ ...form, newPassword: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Min 8 caratteri con maiuscola, minuscola e numero</p>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2"
              >
                {saving ? <div className="animate-spin rounded-full border-2 border-white border-t-transparent w-5 h-5" /> : <Check className="w-5 h-5" />}
                Salva modifiche
              </button>
            </form>
          </div>

          {/* Gestione Consensi GDPR */}
          <div className="bg-white p-6 rounded-2xl shadow-xl border">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Shield className="w-6 h-6" />
              Gestione Privacy (GDPR)
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">Consenso Marketing</h4>
                  <p className="text-sm text-gray-600">
                    Ricevi comunicazioni promozionali e newsletter sui nostri servizi
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={gdprConsents.marketingConsent}
                    onChange={e => updateGdprConsents('marketingConsent', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">Consenso Profilazione</h4>
                  <p className="text-sm text-gray-600">
                    Personalizzazione dell'esperienza e analisi delle preferenze
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={gdprConsents.profilingConsent}
                    onChange={e => updateGdprConsents('profilingConsent', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={exportData}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Esporta i miei dati
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Scarica tutti i tuoi dati personali in formato JSON (diritto GDPR)
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar Informazioni */}
        <div className="space-y-6">
          {/* Status Account */}
          <div className="bg-white p-6 rounded-2xl shadow-xl border">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Status Account</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email verificata</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  user?.emailVerified 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user?.emailVerified ? 'Verificata' : 'Non verificata'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Ruolo</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  user?.role === 'admin' ? 'bg-red-100 text-red-800' :
                  user?.role === 'collaborator' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {user?.role === 'admin' ? 'Amministratore' : 
                   user?.role === 'collaborator' ? 'Collaboratore' : 'Utente'}
                </span>
              </div>
            </div>
          </div>

          {/* Azioni Account */}
          <div className="bg-white p-6 rounded-2xl shadow-xl border">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Azioni Account</h3>
            <div className="space-y-3">
              <button
                onClick={logout}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Esci
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <UserX className="w-4 h-4" />
                Elimina Account
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              L'eliminazione dell'account rimuoverà tutti i tuoi dati permanentemente
            </p>
          </div>
        </div>
      </div>

      {/* Modal Eliminazione Account */}
      {showDeleteModal && (
        <DeleteAccountModal 
          onClose={() => setShowDeleteModal(false)}
          token={token}
          onDeleted={() => {
            showMsg('Account eliminato');
            logout();
          }}
        />
      )}
    </div>
  );
};

export default ProfileView;